/**
 * Create or edit a Discover Pakistan point with photos and cover selection.
 */
import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import ImageUpload from '../common/ImageUpload';
import ImageZoomPreviewModal from '../common/ImageZoomPreviewModal';
import hotelService from '../../services/hotelService';
import pointService from '../../services/pointService';
import { imageService } from '../../services/imageService';
import { imageFieldUrl } from '../../utils/imageFallback';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';

const MAX_POINT_IMAGES = 20;

const PointForm = ({ mode = 'create', initialPoint = null }) => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { showSuccess, showError } = useToast();
  const isEditMode = mode === 'edit' && initialPoint;

  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [selectedImages, setSelectedImages] = useState([]);
  const [existingImages, setExistingImages] = useState([]);
  const [imagesToRemove, setImagesToRemove] = useState([]);
  const [displayImageIndex, setDisplayImageIndex] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const imagePreviewRef = useRef(null);

  const [formData, setFormData] = useState({
    name: '',
    point_category_id: '',
    city: '',
    area: '',
    address: '',
    short_description: '',
    description: '',
    best_visit_months: '',
    is_free: false,
  });

  const setImagePreviewSafe = useCallback((next) => {
    setImagePreview((prev) => {
      if (prev?.revokeOnClose && prev.url) URL.revokeObjectURL(prev.url);
      return next;
    });
  }, []);

  useEffect(() => {
    imagePreviewRef.current = imagePreview;
  }, [imagePreview]);

  useEffect(() => () => {
    const p = imagePreviewRef.current;
    if (p?.revokeOnClose && p.url) URL.revokeObjectURL(p.url);
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return undefined;
    }
    let cancelled = false;
    hotelService
      .getPointCategories()
      .then((raw) => {
        if (cancelled) return;
        const list = Array.isArray(raw) ? raw : raw?.results ?? raw?.data ?? [];
        setCategories(Array.isArray(list) ? list : []);
      })
      .catch(() => {
        if (!cancelled) setCategories([]);
      });
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    if (!isEditMode || !initialPoint) return;
    setFormData({
      name: initialPoint.name || '',
      point_category_id: initialPoint.point_category?.id
        ? String(initialPoint.point_category.id)
        : '',
      city: initialPoint.city || '',
      area: initialPoint.area || '',
      address: initialPoint.address || '',
      short_description: initialPoint.short_description || '',
      description: initialPoint.description || '',
      best_visit_months: initialPoint.best_visit_months || '',
      is_free: !!initialPoint.is_free,
    });
    const imgs = Array.isArray(initialPoint.images) ? initialPoint.images : [];
    setExistingImages(
      imgs
        .map((im) => {
          const url = imageFieldUrl(im?.original_image);
          return url && im.id != null ? { id: im.id, url } : null;
        })
        .filter(Boolean),
    );
    setSelectedImages([]);
    setImagesToRemove([]);
    if (imgs.length) {
      const featured = imgs.find((img) => img.is_featured);
      const coverId = featured?.id;
      if (coverId != null) {
        const idx = imgs.findIndex((img) => img.id === coverId);
        if (idx >= 0) setDisplayImageIndex(idx);
      } else {
        setDisplayImageIndex(0);
      }
    } else {
      setDisplayImageIndex(null);
    }
  }, [isEditMode, initialPoint]);

  const keptExistingImages = existingImages.filter((img) => !imagesToRemove.includes(img.id));

  const remapDisplayIndexAfterRemove = (removedGlobalIndex) => {
    setDisplayImageIndex((prev) => {
      if (prev === null) return null;
      const remainingCount = keptExistingImages.length - 1 + selectedImages.length;
      if (prev === removedGlobalIndex) {
        return remainingCount > 0 ? 0 : null;
      }
      if (prev > removedGlobalIndex) return prev - 1;
      return prev;
    });
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const buildPayload = () => {
    const payload = {
      name: (formData.name || '').trim(),
      is_free: formData.is_free ? 'true' : 'false',
      images: selectedImages,
    };
    if (formData.point_category_id) {
      payload.point_category_id = formData.point_category_id;
    }
    ['city', 'area', 'address', 'short_description', 'description', 'best_visit_months'].forEach((key) => {
      const val = (formData[key] || '').trim();
      if (val) payload[key] = val;
    });
    const totalImages = keptExistingImages.length + selectedImages.length;
    let coverIndex = displayImageIndex;
    if (totalImages > 0 && coverIndex === null) coverIndex = 0;
    if (coverIndex !== null && coverIndex >= totalImages) coverIndex = 0;
    if (coverIndex !== null) payload.display_image_index = coverIndex;
    return payload;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const name = (formData.name || '').trim();
    if (!name) {
      showError('Name is required.');
      return;
    }

    setLoading(true);
    try {
      const payload = buildPayload();
      if (isEditMode && initialPoint?.slug) {
        for (const imageId of imagesToRemove) {
          await imageService.deleteImage(imageId);
        }
        const updated = await pointService.updatePoint(initialPoint.slug, payload);
        const data = updated?.data ?? updated;
        showSuccess('Point updated.');
        navigate(`/points/${data?.slug || initialPoint.slug}`);
        return;
      }

      const response = await pointService.createPoint(payload);
      const created = response?.data ?? response;
      showSuccess('Point submitted. It will appear after review.');
      const slug = created?.slug;
      navigate(slug ? `/points/${slug}` : '/points');
    } catch (err) {
      showError(err?.message || err?.response?.data?.message || 'Could not save point.');
    } finally {
      setLoading(false);
    }
  };

  if (!isAuthenticated) return null;

  return (
    <>
      <form onSubmit={handleSubmit} className="space-y-4 bg-white rounded-xl border border-gray-200 p-4 sm:p-6 shadow-sm">
        <div>
          <label htmlFor="point-name" className="block text-sm font-medium text-gray-700 mb-1">
            Name *
          </label>
          <input
            id="point-name"
            name="name"
            type="text"
            required
            value={formData.name}
            onChange={handleChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-md"
          />
        </div>

        <div>
          <label htmlFor="point-category" className="block text-sm font-medium text-gray-700 mb-1">
            Category
          </label>
          <select
            id="point-category"
            name="point_category_id"
            value={formData.point_category_id}
            onChange={handleChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white"
          >
            <option value="">Select category</option>
            {categories.map((cat) => (
              <option key={cat.id} value={cat.id}>
                {cat.icon ? `${cat.icon} ` : ''}{cat.name}
              </option>
            ))}
          </select>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label htmlFor="point-city" className="block text-sm font-medium text-gray-700 mb-1">City</label>
            <input id="point-city" name="city" type="text" value={formData.city} onChange={handleChange} className="w-full px-3 py-2 border border-gray-300 rounded-md" />
          </div>
          <div>
            <label htmlFor="point-area" className="block text-sm font-medium text-gray-700 mb-1">Area</label>
            <input id="point-area" name="area" type="text" value={formData.area} onChange={handleChange} className="w-full px-3 py-2 border border-gray-300 rounded-md" />
          </div>
        </div>

        <div>
          <label htmlFor="point-address" className="block text-sm font-medium text-gray-700 mb-1">Address</label>
          <input id="point-address" name="address" type="text" value={formData.address} onChange={handleChange} className="w-full px-3 py-2 border border-gray-300 rounded-md" />
        </div>

        <div>
          <label htmlFor="point-short" className="block text-sm font-medium text-gray-700 mb-1">Short description</label>
          <input id="point-short" name="short_description" type="text" maxLength={255} value={formData.short_description} onChange={handleChange} className="w-full px-3 py-2 border border-gray-300 rounded-md" />
        </div>

        <div>
          <label htmlFor="point-desc" className="block text-sm font-medium text-gray-700 mb-1">Description</label>
          <textarea id="point-desc" name="description" rows={4} value={formData.description} onChange={handleChange} className="w-full px-3 py-2 border border-gray-300 rounded-md" />
        </div>

        <div>
          <label htmlFor="point-months" className="block text-sm font-medium text-gray-700 mb-1">Best time to visit</label>
          <input id="point-months" name="best_visit_months" type="text" value={formData.best_visit_months} onChange={handleChange} className="w-full px-3 py-2 border border-gray-300 rounded-md" />
        </div>

        <label className="flex items-center gap-2 cursor-pointer">
          <input name="is_free" type="checkbox" checked={formData.is_free} onChange={handleChange} />
          <span className="text-sm text-gray-800">Free entry</span>
        </label>

        <div>
          {isEditMode && keptExistingImages.length > 0 && (
            <div className="mb-4">
              <span className="block text-sm font-medium text-gray-700 mb-2">
                Current photos (set cover, zoom, or remove)
              </span>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {keptExistingImages.map((img, index) => {
                  const isCover = displayImageIndex === index;
                  return (
                    <div key={img.id} className="relative">
                      <img
                        src={img.url}
                        alt=""
                        className={`w-full h-24 object-cover rounded-lg border-2 ${
                          isCover ? 'border-emerald-600 ring-2 ring-emerald-200' : 'border-gray-200'
                        }`}
                      />
                      <button
                        type="button"
                        onClick={() => setDisplayImageIndex(index)}
                        disabled={loading}
                        className={`absolute top-1 left-1 px-1.5 py-0.5 rounded text-xs font-medium ${
                          isCover ? 'bg-emerald-700 text-white' : 'bg-white/90 text-gray-700'
                        }`}
                      >
                        {isCover ? 'Cover' : 'Set cover'}
                      </button>
                      <button
                        type="button"
                        onClick={() =>
                          setImagePreviewSafe({
                            url: img.url,
                            name: `Photo ${index + 1}`,
                            revokeOnClose: false,
                          })
                        }
                        className="absolute bottom-1 left-1 rounded bg-black/55 px-2 py-0.5 text-[11px] text-white"
                      >
                        Zoom
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setImagesToRemove((prev) => [...prev, img.id]);
                          remapDisplayIndexAfterRemove(index);
                        }}
                        disabled={loading}
                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 text-sm"
                      >
                        ×
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <span className="block text-sm font-medium text-gray-700 mb-2">
            {isEditMode ? 'Add more photos' : 'Photos'}
          </span>
          <ImageUpload
            selectedImages={selectedImages}
            onImagesChange={setSelectedImages}
            maxImages={Math.max(0, MAX_POINT_IMAGES - keptExistingImages.length)}
            autoCompress
            disabled={loading}
            displayImageIndex={displayImageIndex}
            onDisplayImageIndexChange={setDisplayImageIndex}
            imageIndexOffset={keptExistingImages.length}
            onError={(msg) => {
              if (msg) showError(msg);
            }}
          />
        </div>

        <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={() => navigate(isEditMode && initialPoint?.slug ? `/points/${initialPoint.slug}` : '/points')}
            disabled={loading}
            className="px-4 py-2 rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-4 py-2 rounded-md bg-emerald-700 text-white font-medium hover:bg-emerald-800 disabled:opacity-50"
          >
            {loading ? 'Saving…' : isEditMode ? 'Save changes' : 'Submit Point'}
          </button>
        </div>
      </form>

      <ImageZoomPreviewModal
        isOpen={!!imagePreview}
        imageUrl={imagePreview?.url}
        imageName={imagePreview?.name}
        onClose={() => setImagePreviewSafe(null)}
      />
    </>
  );
};

export default PointForm;
